import React, { useState } from 'react';
import styled from 'styled-components';
import { FiSearch, FiFilter, FiX, FiDownload } from 'react-icons/fi';
import theme from '../../styles/theme';
import { contatosAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const FiltrosContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[6]};
`;

const FiltrosHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[6]};
`;

const Title = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
`;

const ActionButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ImportButton = styled(ActionButton)`
  background: ${theme.colors.primary[500]};
  color: white;
  border-color: ${theme.colors.primary[500]};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[600]};
    border-color: ${theme.colors.primary[600]};
    color: white;
  }
`;

const FiltrosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FiltroGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }
`;

const ContatosFiltros = ({ filtros, onFiltrosChange, onBuscar, onBuscarWithFilters, loading, showClienteFilter = true, clientes = [], onImportarSuccess }) => {
  const { isAdmin } = useAuth();
  const [importando, setImportando] = useState(false);
  
  const clienteUnicoId = clientes.length === 1 && clientes[0]?.id != null
    ? clientes[0].id.toString()
    : '';

  const handleInputChange = (field, value) => {
    // Apenas atualizar o estado local, sem buscar
    const novosFiltros = {
      ...filtros,
      [field]: value,
      page: 1 // Reset para primeira página
    };
    
    // Apenas atualizar estado, não buscar ainda
    if (onFiltrosChange) {
      onFiltrosChange(novosFiltros);
    }
  };

  const handleClienteChange = (value) => {
    // Apenas atualizar o estado local, sem buscar
    const novosFiltros = {
      ...filtros,
      id_cliente: value,
      page: 1 // Reset para primeira página
    };
    
    // Apenas atualizar estado, não buscar ainda
    if (onFiltrosChange) {
      onFiltrosChange(novosFiltros);
    }
  };

  const handleBuscar = () => {
    if (onBuscar) {
      onBuscar();
    }
  };

  const limparFiltros = () => {
    const filtrosLimpos = {
      search: '',
      id_cliente: clienteUnicoId,
      page: 1,
      limit: filtros.limit || 10
    };
    
    // Se tiver onBuscarWithFilters, usar ele passando os filtros limpos diretamente
    if (onBuscarWithFilters) {
      onBuscarWithFilters(filtrosLimpos);
    } else {
      // Caso contrário, atualizar estado e depois buscar passando os filtros limpos diretamente
      if (onFiltrosChange) {
        onFiltrosChange(filtrosLimpos);
      }
      // Executar busca automaticamente após limpar filtros, passando os filtros limpos diretamente
      if (onBuscar) {
        onBuscar(filtrosLimpos);
      }
    }
  };

  const handleImportar = async () => {
    try {
      setImportando(true);
      
      // Se for admin, precisa ter um cliente selecionado
      let idCliente = null;
      if (isAdmin()) {
        if (!filtros.id_cliente || filtros.id_cliente === '') {
          toast.error('Selecione um cliente para importar contatos');
          setImportando(false);
          return;
        }
        idCliente = parseInt(filtros.id_cliente);
      }

      const response = await contatosAPI.importar(idCliente);
      
      if (response.data.success) {
        const { importados, atualizados, total } = response.data.data;
        toast.success(
          `Importação concluída! ${importados} novos, ${atualizados} atualizados de ${total} contatos encontrados.`
        );
        
        // Recarregar lista de contatos
        if (onBuscar) {
          onBuscar();
        }
        
        // Callback de sucesso
        if (onImportarSuccess) {
          onImportarSuccess(response.data.data);
        }
      } else {
        toast.error(response.data.error || 'Erro ao importar contatos');
      }
    } catch (error) {
      console.error('Erro ao importar contatos:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao importar contatos';
      toast.error(errorMessage);
    } finally {
      setImportando(false);
    }
  };

  const temFiltrosAtivos = Object.values(filtros).some(value => value !== '');

  return (
    <FiltrosContainer>
      <FiltrosHeader>
        <Title>
          <FiFilter />
          Filtros de Busca
        </Title>
        <Actions>
          <ImportButton onClick={handleImportar} disabled={loading || importando}>
            <FiDownload />
            {importando ? 'Importando...' : 'Importar Contatos'}
          </ImportButton>
          <ActionButton onClick={limparFiltros} disabled={!temFiltrosAtivos}>
            <FiX />
            Limpar Filtros
          </ActionButton>
          <ActionButton onClick={handleBuscar} disabled={loading}>
            <FiSearch />
            {loading ? 'Buscando...' : 'Buscar'}
          </ActionButton>
        </Actions>
      </FiltrosHeader>

      <FiltrosGrid>
        {/* Busca geral */}
        <FiltroGroup>
          <Label>Busca Geral</Label>
          <Input
            type="text"
            placeholder="Nome, sessão..."
            value={filtros.search}
            onChange={(e) => handleInputChange('search', e.target.value)}
          />
        </FiltroGroup>

        {/* Cliente - mostrar se showClienteFilter for true OU se for admin */}
        {(showClienteFilter || isAdmin()) && (
          <FiltroGroup>
            <Label>Cliente</Label>
            <Select
              value={filtros.id_cliente}
              onChange={(e) => handleClienteChange(e.target.value)}
            >
              <option value="">Todos os clientes</option>
              {clientes.map((cliente) => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.nome_fantasia || cliente.nome_social}
                </option>
              ))}
            </Select>
          </FiltroGroup>
        )}
      </FiltrosGrid>
    </FiltrosContainer>
  );
};

export default ContatosFiltros;

